// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
// TODO. Supports stop/limit pairs so far.
// Refactor and delete OCOCustomOrdersEdit class.
// Currently supports Stop/Limit pair only.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { OperationType } from '@shared/utils/Trading/OperationType';
import { OrderEditBaseUtils } from '@shared/utils/Trading/OrderEditBaseUtils';
import { OrderEditUpdateData } from '@shared/utils/Trading/OrderEditUpdateData';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { OrderUtils } from '@shared/utils/Trading/OrderUtils';
import { DynProperty } from '../../../DynProperty';
import { BaseSettings } from '../../../Settings/BaseGeneralSettingsWrapper';
import { OrderExecutorUtils } from '../../../Trading/OrderExecutorUtils';
import { Resources } from '@shared/localizations/Resources';
import { OrderEditConfirmationTextParams } from '../OrderEditConfirmationTextParams';
import { SLTPEdit, SLTPMode } from '../SLTPEdit';
import { OrderEditBase } from './OrderEditBase';
export class OCOOrderEdit extends OrderEditBase {
    constructor(data) {
        super(data);
        this.needSetDefaultStopPrice = false;
        this.needSetDefaultLimitPrice = false;
        this.stopPrice = null;
        this.limitPrice = null;
        this.sltp = new SLTPEdit(data.dataCache, SLTPMode.Offset);
    }
    getParameterNameArray() {
        return [
            OrderEditBaseUtils.STOP_PRICE_PARAM,
            OrderEditBaseUtils.LIMIT_PRICE_PARAM,
            OrderEditBaseUtils.POSITION_SIZING_PARAM,
            OrderEditBaseUtils.SLTP_PARAM,
            OrderEditBaseUtils.OFFMARKET_ENABLED,
            OrderEditBaseUtils.EST_AVAILABLE_CASH
        ];
    }
    // TODO. Refactor. Same as OrderTypeBase.Id function.
    getOrderTypeId() {
        return OrderType.OCO;
    }
    // TODO. Refactor. Use error/warning dicts for sltp.
    // public valid ()
    // {
    //     return OrderEditBase.prototype.valid.call(this) &&
    //         this.sltp.valid()
    // }
    // #region Parameters' DynProperty Generators
    createPriceDynProperty(price, localizationKey, isStopPrice = false) {
        const dpName = OrderEditBase[isStopPrice ? 'STOP_PRICE_PARAM' : 'LIMIT_PRICE_PARAM'];
        const dp = new DynProperty(dpName);
        dp.type = DynProperty.DOUBLE;
        const ins = this.instrument;
        if (ins) {
            dp.minimalValue = ins.PointSize;
            dp.maximalValue = 999999999;
            dp.decimalPlaces = ins.Precision;
            dp.increment = ins.PointSize;
        }
        dp.value = price;
        if (dp.value === null || isNaN(dp.value)) {
            dp.value = dp.minimalValue;
        }
        dp.localizationKey = localizationKey;
        return dp;
    }
    toDynProperty_stopPrice() {
        return this.createPriceDynProperty(this.stopPrice, 'panel.newOrderEntry.stopPricePanel', true);
    }
    toDynProperty_limitPrice() {
        return this.createPriceDynProperty(this.limitPrice, 'panel.newOrderEntry.limitPricePanel');
    }
    // #endregion Parameters' DynProperty Generators
    // #region Update Parameter Handlers
    // TODO. Refactor. Duplicate of update_limitPrice.
    update_stopPrice(updateData) {
        let parameterChanged = false;
        const dp = updateData.dp;
        if (dp && this.stopPrice !== dp.value) {
            this.stopPrice = dp.value;
            parameterChanged = true || parameterChanged;
        }
        let newInstrument = null;
        let newQuote = null;
        let sideChanged = false;
        const newTradingDataDict = updateData.newTradingDataDict;
        if (newTradingDataDict) {
            newInstrument = newTradingDataDict.instrument;
            newQuote = newTradingDataDict.quote;
            sideChanged = 'side' in newTradingDataDict;
        }
        if (newInstrument) {
            this.stopPrice = null;
            this.needSetDefaultStopPrice = true;
            parameterChanged = true || parameterChanged;
        }
        if (this.needSetDefaultStopPrice && newQuote) {
            this.needSetDefaultStopPrice = false;
            this.stopPrice = newQuote.BidSpread_SP_Ins(this.instrument.DataCache.GetSpreadPlan(this.account), this.instrument);
            parameterChanged = true || parameterChanged;
        }
        if (newQuote || sideChanged || parameterChanged) {
            parameterChanged = this.tryUpdateWarning_stopPrice(this.stopPrice, this.quote) || parameterChanged;
        }
        return parameterChanged;
    }
    // TODO. Refactor. Duplicate of update_stopPrice.
    update_limitPrice(updateData) {
        let parameterChanged = false;
        const dp = updateData.dp;
        if (dp && this.limitPrice !== dp.value) {
            this.limitPrice = dp.value;
            parameterChanged = true || parameterChanged;
        }
        let newInstrument = null;
        let newQuote = null;
        let sideChanged = false;
        const newTradingDataDict = updateData.newTradingDataDict;
        if (newTradingDataDict) {
            newInstrument = newTradingDataDict.instrument;
            newQuote = newTradingDataDict.quote;
            sideChanged = 'side' in newTradingDataDict;
        }
        if (newInstrument) {
            this.limitPrice = null;
            this.needSetDefaultLimitPrice = true;
            parameterChanged = true || parameterChanged;
        }
        if (this.needSetDefaultLimitPrice && newQuote) {
            this.needSetDefaultLimitPrice = false;
            this.limitPrice = newQuote.BidSpread_SP_Ins(this.instrument.DataCache.GetSpreadPlan(this.account), this.instrument);
            parameterChanged = true || parameterChanged;
        }
        if (newQuote || sideChanged || parameterChanged) {
            parameterChanged = this.tryUpdateWarning_limitPrice(this.limitPrice, this.quote) || parameterChanged;
        }
        return parameterChanged;
    }
    // TODO. Duplicate of tryUpdateWarning_limitPrice.
    tryUpdateWarning_stopPrice(stopPrice, quote) {
        if (!quote || !stopPrice) {
            return false;
        }
        const sp = this.instrument.DataCache.GetSpreadPlan(this.account);
        const ask = quote.AskSpread_SP_Ins(sp, this.instrument);
        const bid = quote.BidSpread_SP_Ins(sp, this.instrument);
        const buy = this.buy();
        if (this.firstOrderType || this.limitPrice == null) {
            return false;
        } // todo warning for custom OCO
        const newWarning = buy && stopPrice < ask
            ? 'general.trading.stopBuyMoreAsk'
            : !buy && stopPrice > bid
                ? 'general.trading.stopSellLessBid'
                : null;
        return this.warningsManager.setParameterWarning(OrderEditBaseUtils.STOP_PRICE_PARAM, newWarning);
    }
    // TODO. Duplicate of tryUpdateWarning_stopPrice.
    tryUpdateWarning_limitPrice(limitPrice, quote) {
        if (!quote || !limitPrice) {
            return false;
        }
        const sp = this.instrument.DataCache.GetSpreadPlan(this.account);
        const ask = quote.AskSpread_SP_Ins(sp, this.instrument);
        const bid = quote.BidSpread_SP_Ins(sp, this.instrument);
        const buy = this.buy();
        if (this.firstOrderType) {
            return false;
        } // todo warning for custom OCO
        const newWarning = buy && limitPrice > ask
            ? 'general.trading.limitBuyLessAsk'
            : !buy && limitPrice < bid
                ? 'general.trading.limitSellMoreBid'
                : null;
        return this.warningsManager.setParameterWarning(OrderEditBaseUtils.LIMIT_PRICE_PARAM, newWarning);
    }
    // #endregion Update Parameter Handlers
    // #region Raw Values
    toRawValue_stopPrice() {
        return this.stopPrice;
    }
    toRawValue_limitPrice() {
        return this.limitPrice;
    }
    // #endregion Raw Values
    validateParameters() {
        const updatedParamNameDict = {};
        if (this.sltp.validate(null, null, this.getTradingData())) {
            updatedParamNameDict[OrderEditBaseUtils.SLTP_PARAM] = true;
        }
        updatedParamNameDict[OrderEditBaseUtils.POSITION_SIZING_PARAM] = true;
        return updatedParamNameDict;
    }
    getDataForRequest() {
        const data = super.getDataForRequest();
        data.useStopLimitInsteadStop = this.useStopLimitInsteadStop();
        if (data.useStopLimitInsteadStop) {
            data.limitPriceForStopLimitOrder = this.getLimitPriceWithDefaultOffset(this.toRawValue_stopPrice());
        }
        return data;
    }
    useStopLimitInsteadStop() {
        // для удобства вызова, чтоб не лазить глубоко в сеттинги
        return BaseSettings.isUseStopLimitInsteadStop();
    }
    getConfirmationText() {
        return __awaiter(this, arguments, void 0, function* (sendMarginRequest = true) {
            if (sendMarginRequest)
                yield this.ConfirmationMarginRequest();
            const firstOrderParams = this.getFirstOrderParameters();
            const firstTextParams = new OrderEditConfirmationTextParams();
            firstTextParams.OrderTypeKey = OrderUtils.getOrderTypeLocalizationKey(firstOrderParams.orderType);
            firstTextParams.Tif = firstOrderParams.tif;
            firstTextParams.Instrument = this.instrument;
            firstTextParams.Account = this.account;
            firstTextParams.Quantity = this.quantity;
            firstTextParams.Buy = firstOrderParams.operation === OperationType.Buy;
            firstTextParams.ProductType = this.productType;
            firstTextParams.OrderType = firstOrderParams.orderType;
            firstTextParams.LeverageValue = this.getLeverageValue();
            firstTextParams.IsAllowOTHTrading = this.isAllowOTHTrading;
            const firstOrderParamsPriceArray = [firstOrderParams.price];
            if (isValidNumber(firstOrderParams.stopLimit)) {
                firstOrderParamsPriceArray.push(firstOrderParams.stopLimit);
            }
            firstTextParams.PriceArray = firstOrderParamsPriceArray;
            let firstPart = OrderExecutorUtils.buildOrderEditConfirmationText(firstTextParams);
            firstPart = firstPart.replace('?', '').trim();
            const secondOrderParams = this.getSecondOrderParameters();
            const secondTextParams = new OrderEditConfirmationTextParams();
            secondTextParams.OrderTypeKey = OrderUtils.getOrderTypeLocalizationKey(secondOrderParams.orderType);
            secondTextParams.Tif = secondOrderParams.tif;
            secondTextParams.Instrument = this.instrument;
            secondTextParams.Account = this.account;
            secondTextParams.Quantity = this.quantity;
            secondTextParams.Buy = secondOrderParams.operation === OperationType.Buy;
            secondTextParams.ProductType = this.productType;
            secondTextParams.OrderType = secondOrderParams.orderType;
            secondTextParams.LeverageValue = this.getLeverageValue();
            secondTextParams.IsAllowOTHTrading = this.isAllowOTHTrading;
            const secondOrderParamsPriceArray = [secondOrderParams.price];
            if (isValidNumber(secondOrderParams.stopLimit)) {
                secondOrderParamsPriceArray.push(secondOrderParams.stopLimit);
            }
            secondTextParams.PriceArray = secondOrderParamsPriceArray;
            const secondPart = OrderExecutorUtils.buildOrderEditConfirmationText(secondTextParams);
            const sltpPart = this.sltp.getConfirmationText(this.getTradingData());
            return (Resources.getResource('OCO') +
                ' ' +
                Resources.getResource('property.protrader.showOrders') +
                ' ' +
                Resources.getResource('general.trading.for') +
                ' ' +
                this.account.toString() +
                ':\n' +
                firstPart +
                ';\n' +
                secondPart +
                (sltpPart ? '\n' + sltpPart : ''));
        });
    }
    getOrderConfirmationMarginInfoParameters() {
        const firstOrderParams = this.getFirstOrderParameters();
        const firstMarginParams = this.getDataForRequest().GetMarginParameters();
        firstMarginParams.operation = firstOrderParams.operation;
        firstMarginParams.orderType = firstOrderParams.orderType;
        ((firstMarginParams.validity = firstOrderParams.tif), (firstMarginParams.price = firstOrderParams.price));
        firstMarginParams.stopPrice = firstOrderParams.stopLimit;
        const secondOrderParams = this.getSecondOrderParameters();
        const secondMarginParams = this.getDataForRequest().GetMarginParameters();
        secondMarginParams.operation = secondOrderParams.operation;
        secondMarginParams.orderType = secondOrderParams.orderType;
        ((secondMarginParams.validity = secondOrderParams.tif), (secondMarginParams.price = secondOrderParams.price));
        secondMarginParams.stopPrice = secondOrderParams.stopLimit;
        const reslult = [];
        const weakLeg = this.getWeakLegMarginInfoParam(firstMarginParams, secondMarginParams);
        reslult.push({ isOnlyOffMarket: weakLeg === firstMarginParams, marginInfoParameters: firstMarginParams });
        reslult.push({ isOnlyOffMarket: weakLeg === secondMarginParams, marginInfoParameters: secondMarginParams });
        return reslult;
    }
    getFirstOrderParameters() {
        const operation = !isNullOrUndefined(this.firstOrderSide) ? this.firstOrderSide : this.side;
        const orderType = !isNullOrUndefined(this.firstOrderType) ? this.firstOrderType : OrderType.Limit;
        const tif = !isNullOrUndefined(this.firstOrderTif) ? this.firstOrderTif : this.tif;
        const orderPrice = this.getOrderPrice(operation, orderType);
        return {
            operation,
            orderType,
            tif,
            price: orderPrice.price,
            stopLimit: orderPrice.stopLimit
        };
    }
    getSecondOrderParameters() {
        const operation = !isNullOrUndefined(this.secondOrderSide) ? this.secondOrderSide : this.side;
        const orderType = !isNullOrUndefined(this.secondOrderType)
            ? this.secondOrderType
            : this.useStopLimitInsteadStop()
                ? OrderType.StopLimit
                : OrderType.Stop;
        const tif = !isNullOrUndefined(this.secondOrderTif) ? this.secondOrderTif : this.tif;
        const orderPrice = this.getOrderPrice(operation, orderType);
        return {
            operation,
            orderType,
            tif,
            price: orderPrice.price,
            stopLimit: orderPrice.stopLimit
        };
    }
    getOrderPrice(operation, orderType) {
        let price = null;
        let stopLimit = null;
        switch (orderType) {
            case OrderType.Limit:
                price = this.limitPrice;
                break;
            case OrderType.Stop:
                price = this.stopPrice;
                break;
            case OrderType.StopLimit:
                price = this.getLimitPriceWithDefaultOffset(this.stopPrice, operation === OperationType.Buy);
                stopLimit = this.stopPrice;
                break;
        }
        return { price, stopLimit };
    }
    getWeakLegMarginInfoParam(firstOrder, secondOrder) {
        const firstOrderPrice = isValidNumber(firstOrder.price) ? firstOrder.price : firstOrder.stopPrice;
        const secondOrderPrice = isValidNumber(secondOrder.price) ? secondOrder.price : secondOrder.stopPrice;
        return firstOrderPrice <= secondOrderPrice ? firstOrder : secondOrder;
    }
    setBasePrice(price) {
        const dpLimitPrice = this.createDynPropertyFromParameter(OrderEditBaseUtils.LIMIT_PRICE_PARAM);
        dpLimitPrice.value = price;
        const dpStopPrice = this.createDynPropertyFromParameter(OrderEditBaseUtils.STOP_PRICE_PARAM);
        dpStopPrice.value = this.stopPrice;
        this.updateParameters(new OrderEditUpdateData({
            limitPrice: dpLimitPrice,
            stopPrice: dpStopPrice
        }));
    }
    setStopPrice(price) {
        const dpLimitPrice = this.createDynPropertyFromParameter(OrderEditBaseUtils.LIMIT_PRICE_PARAM);
        dpLimitPrice.value = this.limitPrice;
        const dpStopPrice = this.createDynPropertyFromParameter(OrderEditBaseUtils.STOP_PRICE_PARAM);
        dpStopPrice.value = price;
        this.updateParameters(new OrderEditUpdateData({
            limitPrice: dpLimitPrice,
            stopPrice: dpStopPrice
        }));
    }
    getBasePrice() {
        // for #101148
        return this.limitPrice;
    }
    skipParamForFlashing(paramName) {
        // при включенном positionSizing надо ли DynPropertyControl c paramName пропускать при подсветке лейблов (к примеру как Stop price не подсвечиваем у Stop limit-a)
        if (paramName === OrderEditBaseUtils.STOP_PRICE_PARAM) {
            return true;
        }
        return false;
    }
}
